import type { PrefetchContext, PrefetchedDataProps } from 'src/shared/prefetch';

import { useCountryCode, useLocale } from 'src/app/shared/hooks/l10n';
import { useCurrentUserId } from 'src/app/shared/hooks/session';
import type { MergedState } from 'src/app/shared/hooks/state';
import { useMergedStates } from 'src/app/shared/hooks/state';

import { prefetchDecisionMakerResponsePageDataFromApi, useDecisionMakerResponsePageDataFromApi } from './pageData';
import { prefetchDecisionMakerResponsePageFcm, useAsyncDecisionMakerResponsePageFcm } from './pageFcm';
import {
	prefetchDecisionMakerResponsePageFcmExperiments,
	useAsyncDecisionMakerResponsePageFcmExperiments,
} from './pageFcmExperiments';

export type DecisionMakerResponsePagePrefetchedContext = Readonly<{
	pageData: Parameters<typeof useDecisionMakerResponsePageDataFromApi>[0];
}>;

export type DecisionMakerResponsePageCombinedState = MergedState<
	ReturnType<typeof useDecisionMakerResponsePageDataFromApi>,
	ReturnType<typeof useAsyncDecisionMakerResponsePageFcmExperiments>,
	ReturnType<typeof useAsyncDecisionMakerResponsePageFcm>
>;

export function usePageContext(
	petitionSlugOrId: string,
	responseId: string,
	prefetchedData?: DecisionMakerResponsePagePrefetchedContext,
): DecisionMakerResponsePageCombinedState {
	const userId = useCurrentUserId();
	const locale = useLocale();
	const countryCode = useCountryCode();
	return useMergedStates(
		useDecisionMakerResponsePageDataFromApi(prefetchedData?.pageData, {
			petitionSlugOrId,
			responseId,
			userId,
			locale,
			countryCode,
		}),
		useAsyncDecisionMakerResponsePageFcmExperiments(),
		useAsyncDecisionMakerResponsePageFcm(),
	);
}

export async function prefetchPageContext(
	petitionSlugOrId: string,
	responseId: string,
	context: PrefetchContext,
): Promise<DecisionMakerResponsePagePrefetchedContext> {
	const [pageData] = await Promise.all([
		prefetchDecisionMakerResponsePageDataFromApi(context, {
			petitionSlugOrId,
			responseId,
			userId: context.session?.user?.id,
			...context.l10n,
		}),
		prefetchDecisionMakerResponsePageFcmExperiments(context),
		prefetchDecisionMakerResponsePageFcm(context),
	]);

	return { pageData };
}

export type DecisionMakerResponsePagePrefetchedDataProps =
	PrefetchedDataProps<DecisionMakerResponsePagePrefetchedContext>;
